import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Alert, LinearProgress, List, ListItem
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import api from '../../../services/api';

const RISK_TEMPLATE_HEADERS = [
  'Risk Description *', 'Category *', 'Department *', 'Owner *',
  'Date Reviewed', 'KRA At Risk', 'Causes', 'Consequence',
  'Inherent Likelihood', 'Inherent Consequence',
  'Existing Controls', 'Control Effectiveness',
  'Residual Likelihood', 'Residual Consequence',
  'Mitigation Strategy', 'Action Treatment',
  'Method', 'Review Date', 'Status',
  'Likelihood Justification', 'Consequence Justification'
];

const SHE_TEMPLATE_HEADERS = [
  'Description *', 'Activity Category *', 'Department *', 'Owner',
  'Date', 'Quarter', 'Location', 'Staff Group', 'Reference ID',
  'Observations', 'Recommendations', 'Priority', 'Status',
  'Verification', 'Comments'
];

const RISK_SAMPLE = [
  'Failure of critical IT infrastructure', 'Technological', 'Information Technology Services',
  'IT Manager', '2026-05-18', 'Service Delivery', 'Power outage / hardware failure',
  'System downtime and data loss',
  'POSSIBLE', 'MAJOR', 'UPS and backup systems', 'Moderate',
  'UNLIKELY', 'MODERATE', 'Reduce',
  'Implement redundant systems and disaster recovery plan',
  'Risk Monitoring', '2026-08-18', 'Open', 'Based on historical incidents', 'Could affect all departments'
];

const RISK_HEADER_MAP = {
  'Risk Description *': 'risk_description',
  'Category *': 'category',
  'Department *': 'department_id',
  'Owner *': 'owner',
  'Date Reviewed': 'date_reviewed',
  'KRA At Risk': 'kra_at_risk',
  'Causes': 'causes',
  'Consequence': 'consequence',
  'Inherent Likelihood': 'inherent_likelihood',
  'Inherent Consequence': 'inherent_consequence',
  'Existing Controls': 'existing_controls',
  'Control Effectiveness': 'control_effectiveness',
  'Residual Likelihood': 'residual_likelihood',
  'Residual Consequence': 'residual_consequence',
  'Mitigation Strategy': 'mitigation_strategy',
  'Action Treatment': 'action_treatment',
  'Method': 'method',
  'Review Date': 'resolved_by',
  'Status': 'status',
  'Likelihood Justification': 'likelihood_justification',
  'Consequence Justification': 'consequence_justification'
};

const SHE_HEADER_MAP = {
  'Description *': 'description',
  'Activity Category *': 'activity_category',
  'Department *': 'department',
  'Owner': 'owner',
  'Date': 'date',
  'Quarter': 'quarter',
  'Location': 'location',
  'Staff Group': 'staff_group',
  'Reference ID': 'reference_id',
  'Observations': 'observations',
  'Recommendations': 'recommendations',
  'Priority': 'priority',
  'Status': 'status',
  'Verification': 'verification',
  'Comments': 'comments'
};

const SHE_SAMPLE = [
  'Chemical spillage in laboratory', 'Health & Safety', 'Science Faculty', 'Safety Officer',
  '2026-05-18', 'Q2', 'Science Lab Block A', 'Laboratory Staff', 'REF-2026-001',
  'Improper storage of chemicals observed', 'Relocate chemicals to approved storage',
  'High', 'Open', 'Pending inspection', ''
];

const downloadTemplate = (type) => {
  const headers = type === 'risks' ? RISK_TEMPLATE_HEADERS : SHE_TEMPLATE_HEADERS;
  const sample  = type === 'risks' ? RISK_SAMPLE : SHE_SAMPLE;
  const csv = [headers.map(h => `"${h}"`).join(','), sample.map(v => `"${v}"`).join(',')].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = type === 'risks' ? 'risk_import_template.csv' : 'she_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const CsvImportDialog = ({ open, onClose, type, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const endpoint = type === 'risks' ? '/risks/import' : '/she-events/import';
  const label    = type === 'risks' ? 'Risk' : 'SHE';

  const headerMap = type === 'risks' ? RISK_HEADER_MAP : SHE_HEADER_MAP;

  const normalizeFileToCsv = (file) => new Promise((resolve, reject) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (rows.length < 2) { reject(new Error('File appears empty or has no data rows.')); return; }

        const friendlyHeaders = rows[0].map(h => String(h).trim());
        const dbHeaders = friendlyHeaders.map(h => headerMap[h] || h);
        const dataRows = rows.slice(1).filter(r => r.some(c => String(c).trim() !== ''));

        const csvLines = [
          dbHeaders.map(h => `"${h}"`).join(','),
          ...dataRows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
        ];
        resolve(new Blob([csvLines.join('\r\n')], { type: 'text/csv' }));
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    if (ext === 'csv' || ext === 'txt') {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setResult(null);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const csvBlob = await normalizeFileToCsv(file);
      const form = new FormData();
      form.append('file', csvBlob, 'import.csv');
      const res = await api.post(endpoint, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      if (res.data.imported > 0) onSuccess();
    } catch (e) {
      const errData = e.response?.data;
      const detail = errData?.errors?.file?.[0] || errData?.error || errData?.message || e.message || 'Upload failed. Check file format.';
      setError(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)' } }}>
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem', pb: 1 }}>
        Import {label} Records
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', mb: 2 }}>
          Upload a CSV or Excel file using the template below. Columns marked with <strong style={{color:'#f59e0b'}}>*</strong> are required — rows missing these will be skipped and reported.
        </Typography>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => downloadTemplate(type)}
          sx={{ mb: 3, color: '#3b82f6', borderColor: 'rgba(59,130,246,0.4)', textTransform: 'none', borderRadius: '8px', '&:hover': { background: 'rgba(59,130,246,0.08)' } }}
        >
          Download {label} Template
        </Button>

        <Box
          onClick={() => inputRef.current.click()}
          sx={{
            border: '2px dashed rgba(255,255,255,0.15)', borderRadius: '16px', p: 4,
            textAlign: 'center', cursor: 'pointer', mb: 2,
            background: file ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.02)',
            transition: 'all 0.2s',
            '&:hover': { borderColor: '#3b82f6', background: 'rgba(59,130,246,0.05)' }
          }}
        >
          <UploadFileIcon sx={{ fontSize: 36, color: file ? '#10b981' : '#64748b', mb: 1 }} />
          <Typography sx={{ color: file ? '#10b981' : '#94a3b8', fontSize: '0.9rem', fontWeight: 'bold' }}>
            {file ? file.name : 'Click to select a CSV or Excel file (.csv, .xlsx)'}
          </Typography>
          {file && (
            <Typography sx={{ color: '#64748b', fontSize: '0.75rem', mt: 0.5 }}>
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          )}
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.txt" style={{ display: 'none' }} onChange={handleFileChange} />
        </Box>

        {uploading && <LinearProgress sx={{ borderRadius: '4px', mb: 2 }} />}

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

        {result && (
          <Box sx={{ p: 2, background: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography sx={{ color: '#10b981', fontWeight: 'bold', mb: 1 }}>
              Import complete: {result.imported} imported, {result.skipped} skipped
            </Typography>
            {result.errors?.length > 0 && (
              <List dense disablePadding>
                {result.errors.slice(0, 5).map((e, i) => (
                  <ListItem key={i} disableGutters>
                    <Typography sx={{ color: '#f87171', fontSize: '0.75rem' }}>• {e}</Typography>
                  </ListItem>
                ))}
                {result.errors.length > 5 && (
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>…and {result.errors.length - 5} more issues</Typography>
                )}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={handleClose} sx={{ color: '#94a3b8', textTransform: 'none' }}>Close</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          startIcon={<UploadFileIcon />}
          sx={{ background: 'linear-gradient(135deg,#10b981,#059669)', textTransform: 'none', fontWeight: 'bold', borderRadius: '10px', px: 3, '&.Mui-disabled': { background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', opacity: 0.8 } }}
        >
          {uploading ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CsvImportDialog;
